import {SchemeLink} from '../../../applink/SchemeLink';
import {t} from '../../../common/getTranslations';
import {twMerge} from '../../../common/twMerge';
import {useExecuteInView} from '../../../common/useExecuteInView.hook';
import {BadgeType} from '../../../graphql/generated/types';
import {BADGE_TYPE_TO_ICON_TYPE_MAP} from '../../atoms/Icon/constants';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {Image} from '../../atoms/Image/Image';
import {Ranking} from '../../atoms/Ranking/Ranking';
import {RankingType} from '../../atoms/Ranking/RankingType.enum';
import {SeparatorType} from '../../atoms/Separator/SeparatorType.enum';
import {StaticImage} from '../../atoms/StaticImage/StaticImage';
import {StaticImageType} from '../../atoms/StaticImage/StaticImageType.enum';
import {LabelBadge} from '../../molecules/LabelBadge/LabelBadge';
import {LabelBadge16Type} from '../../molecules/LabelBadge/LabelBadge16Type.enum';
import {LabelBadgeSizeType} from '../../molecules/LabelBadge/LabelBadgeSizeType.enum';
import {ThumbnailRatio} from '../../molecules/ThumbnailWithBadges/ThumbnailRatio.constant';
import {ThumbnailType} from '../../molecules/ThumbnailWithBadges/ThumbnailType.enum';
import {Metadata} from '../Metadata/Metadata';
import {FIXED_POSTER_VIEW_WIDTH_PX} from './constants';
import {PosterViewItemProps} from './PosterViewItemProps.interface';

export function PosterViewItem({
  posterSrc,
  badgeList,
  labelBadgeList,
  ageGradeBadge,
  statusBadge,
  subtitleList,
  title,
  altText,
  isFixedSize,
  scheme,
  isAgeRestricted,
  showPlayerIcon,
  imageLazyLoad,
  onInView,
  className,
  rank,
  rankVariation,
  hideRankVariation,
  showDimmedThumbnail,
  discountRateText,
  statusLabelText,
  isSet,
  posterInfoHeightClassName = 'h-76pxr',
  ...props
}: PosterViewItemProps) {
  const ref = useExecuteInView({callback: onInView});

  const mergedBadgeList: BadgeType[] = [
    ...(badgeList ? badgeList : []),
    ...(statusBadge ? [statusBadge] : []),
  ];

  return (
    <SchemeLink scheme={scheme || undefined}>
      <div
        ref={ref}
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={twMerge(
          isFixedSize && 'poster-item-container',
          'relative',
          className
        )}
        aria-label={altText}
        {...props}
      >
        <div className="relative" aria-hidden={true}>
          {isAgeRestricted && (
            <>
              <StaticImage
                imageType={StaticImageType.Age19Thumbnail43}
                className={THUMBNAIL_STYLE}
              />
              {isSet && (
                <LabelBadge
                  sizeType={LabelBadgeSizeType.Size16}
                  type={LabelBadge16Type.black}
                  label={t('sethome_badge_set')}
                  className="absolute left-4pxr top-4pxr"
                />
              )}
            </>
          )}
          {!isAgeRestricted && (
            <Image
              src={posterSrc}
              alt="썸네일"
              className={twMerge(
                THUMBNAIL_STYLE,
                showDimmedThumbnail && 'fill-theme-solid-0 opacity-50'
              )}
              ratio={ThumbnailRatio[ThumbnailType.Poster]}
              isLazy={imageLazyLoad}
            />
          )}
          {!isAgeRestricted && (
            <div className="absolute left-4pxr top-4pxr flex space-x-2pxr">
              {discountRateText && (
                <LabelBadge
                  sizeType={LabelBadgeSizeType.Size16}
                  type={LabelBadge16Type.red}
                  label={discountRateText}
                  className="px-4pxr"
                />
              )}
              {mergedBadgeList.map((badge, idx) => (
                <Icon type={BADGE_TYPE_TO_ICON_TYPE_MAP[badge]} key={idx} />
              ))}
              {/* UP 뱃지 대신 노출되는 statusLabelText가 UP 뱃지와 동일한 순서로 노출되기 위해, UP 뱃지가 위치하던 mergedBadgeList 가장 마지막 자리(뒤)에 배치 */}
              {statusLabelText && (
                <LabelBadge
                  sizeType={LabelBadgeSizeType.Size16}
                  type={LabelBadge16Type.red}
                  label={statusLabelText}
                  className="px-4pxr"
                />
              )}
              {labelBadgeList &&
                labelBadgeList.map((labelBadge, idx) => (
                  <LabelBadge
                    key={idx}
                    sizeType={LabelBadgeSizeType.Size16}
                    type={LabelBadge16Type.black}
                    label={labelBadge}
                    className="px-4pxr"
                  />
                ))}
              {isSet && (
                <LabelBadge
                  sizeType={LabelBadgeSizeType.Size16}
                  type={LabelBadge16Type.black}
                  label={t('sethome_badge_set')}
                />
              )}
            </div>
          )}
          {!isAgeRestricted && ageGradeBadge && (
            <div className="absolute right-4pxr top-4pxr flex space-x-2pxr">
              <Icon type={BADGE_TYPE_TO_ICON_TYPE_MAP[ageGradeBadge]} />
            </div>
          )}
          {!isAgeRestricted && showPlayerIcon && (
            <div className="absolute bottom-0 flex w-full justify-end pb-4px pr-4px">
              <Icon
                className="shrink-0"
                type={IconType.PlayerPlaySolidStatic}
              />
            </div>
          )}
          {/* 썸네일 보더 */}
          <div className="absolute left-0 top-0 h-full w-full rounded-8pxr border-0.5 border-solid border-static-transparent-black-05" />
        </div>
        <div
          className={twMerge(
            'w-full space-y-4pxr pb-4pxr pr-8pxr pt-8pxr',
            posterInfoHeightClassName
          )}
          aria-hidden={true}
        >
          {rank && (
            <Ranking
              type={RankingType.Theme}
              rank={rank}
              rankVariation={rankVariation}
              hideVariation={hideRankVariation}
            />
          )}
          <div className="font-small1 line-clamp-2 break-all text-theme-solid-100">
            {title}
          </div>
          {!rank && (
            <Metadata
              texts={subtitleList}
              separatorType={SeparatorType.Dot}
              className="mt-4pxr"
              aria-hidden={true}
            />
          )}
        </div>
      </div>
      {/* 고정사이즈인 경우 외부 조건에 상관없이 정해진 px의 크기를 사용. */}
      <style jsx>
        {`
          .poster-item-container {
            width: ${FIXED_POSTER_VIEW_WIDTH_PX}px !important;
          }
        `}
      </style>
    </SchemeLink>
  );
}

const THUMBNAIL_STYLE = 'rounded-8pxr overflow-hidden w-full h-full';
